import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy, promedio, conComa } from '../utils/formato';
import { LITROS_POR_TINA } from '../utils/tina';
import { lecturaDeRuta, compararRutas, calificar, llenado,
         diasDeRuta, variacion } from '../utils/ruta';
import { enUnidadDeCompra } from '../utils/insumo';
import { parteDelDia, enlaceDeWhatsApp, diaLargo } from '../utils/reporteRutas';
import { armarParte, dibujarParte, copiarImagen, nombreDelArchivo }
  from '../utils/parteImagen';

// Cómo va cada ruta.
//
// La pregunta de esta pantalla es «¿cuál me está rindiendo?», y tiene una
// trampa: la ruta a la que le toca más búfala saca más kilos por tina SIN HACER
// NADA MEJOR. Por eso el puesto lo decide el CUMPLIMIENTO —kilos reales contra
// los que esa leche debía dar—, que no depende de la mezcla, y los kilos por
// tina van al lado como lo que se habla.

const primeroDelMes = () => hoy().slice(0, 8) + '01';
const MEDALLA = ['🥇', '🥈', '🥉'];

function Rutas() {
  const [desde, setDesde] = useState(primeroDelMes);
  const [hasta, setHasta] = useState(hoy);
  const [datos, setDatos] = useState(null);
  const [insumos, setInsumos] = useState([]);
  const [enBodega, setEnBodega] = useState([]);
  const [error, setError] = useState('');
  const [porDia, setPorDia] = useState([]);
  // LA LECHE QUE ENTRA POR RUTA, que es lo que pidió Gabriel.
  //
  // Va aparte del rendimiento: una cosa es cuánta leche RECIBIÓ la ruta —de
  // los recibos— y otra cuánta se CUAJÓ. No tienen por qué ser iguales, y la
  // diferencia es justamente lo que hay que poder mirar.
  const [leche, setLeche] = useState(null);
  // Los gastos que se están pactando. Se editan aquí porque es la pantalla de
  // las rutas: buscarlos en otra parte es la manera de que nadie los ponga.
  const [gastos, setGastos] = useState(null);
  const [guardandoGastos, setGuardandoGastos] = useState(false);
  const [abierta, setAbierta] = useState(null);
  // El parte de HOY, aparte de lo que se esté mirando arriba.
  //
  // La pantalla abre en el mes entero y el parte es del día: si se armara con
  // lo que hay en pantalla, un día que alguien cambie las fechas para revisar
  // la semana pasada, el muchacho le mandaría a Gabriel el promedio de otro
  // periodo sin enterarse.
  const [deHoy, setDeHoy] = useState(null);
  const [ajustes, setAjustes] = useState(null);
  const [verParte, setVerParte] = useState(false);
  // El parte dibujado. Gabriel lo quiere como FOTO, no como texto: un enlace de
  // WhatsApp solo puede llevar texto, así que la imagen se copia aquí y él la
  // pega en el chat.
  const [imagen, setImagen] = useState(null);        // {url, blob}
  const [copiado, setCopiado] = useState(false);
  // La utilidad de cada ruta: lo que produjo menos lo que costó.
  const [utilidad, setUtilidad] = useState(null);
  // Los precios a los que se valora cada producto, con la fecha desde la que
  // rigen. Es lo que Gabriel fija por temporada.
  const [precios, setPrecios] = useState([]);
  const [productos, setProductos] = useState([]);
  const [fijando, setFijando] = useState(null);   // {producto_id, desde, precio}
  const [guardandoPrecio, setGuardandoPrecio] = useState(false);

  const cargar = useCallback(() => {
    Promise.all([
      axios.get('/api/centros/rendimiento', { params: { desde, hasta } }),
      axios.get('/api/centros/insumos', { params: { desde, hasta } }),
      axios.get('/api/insumos/por-centro'),
      axios.get('/api/centros/por-dia', { params: { desde, hasta } }),
      axios.get('/api/centros/leche', { params: { desde, hasta } }),
      axios.get('/api/centros/utilidad', { params: { desde, hasta } }),
      axios.get('/api/precios-referencia'),
      axios.get('/api/productos'),
    ])
      .then(([r, i, b, d, l, u, pr, pd]) => {
        setPrecios(pr.data || []); setProductos(pd.data || []);
        setDatos(r.data); setInsumos(i.data || []); setEnBodega(b.data || []);
        setPorDia(d.data || []); setLeche(l.data || null);
        setUtilidad(u.data || null); setError('');
      })
      .catch(() => setError('No se pudo cargar. Recargue la página; si sigue igual, avise.'));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  // DE QUÉ DÍA ES EL PARTE.
  //
  // Hoy, salvo que arriba se haya elegido UN día concreto —desde y hasta
  // iguales—, y entonces es el de ese día. Se probó fijándolo siempre a hoy y
  // le quitaba algo legítimo: el muchacho pone las fechas del 18 para revisar
  // ese día, y el parte le sale apagado diciendo que hoy no hay cuajadas.
  //
  // Un rango de varios días NO arma parte: «el promedio de la semana» mandado
  // como si fuera el del día es la confusión que hay que evitar, y por eso el
  // título siempre dice de cuándo es lo que se va a mandar.
  const diaDelParte = desde === hasta ? desde : hoy();

  useEffect(() => {
    axios.get('/api/centros/rendimiento', { params: { desde: diaDelParte, hasta: diaDelParte } })
      .then(r => setDeHoy((r.data && r.data.centros) || []))
      .catch(() => setDeHoy([]));
  }, [diaDelParte]);

  useEffect(() => {
    axios.get('/api/ajustes').then(r => setAjustes(r.data || {})).catch(() => setAjustes({}));
  }, []);

  const esHoy = diaDelParte === hoy();
  const opcionesDelParte = { negocio: (ajustes && ajustes.nombre_negocio) || '',
                             dia: diaDelParte };
  const parte = deHoy ? parteDelDia(deHoy, opcionesDelParte) : null;

  // La imagen se vuelve a dibujar cada vez que cambia el día o la producción.
  // Va en un efecto y no al vuelo porque `toBlob` es asíncrono, y porque hay
  // que soltar la anterior: cada imagen que se queda sin soltar es memoria que
  // no vuelve, y esta pantalla se deja abierta todo el día.
  const negocioDelParte = opcionesDelParte.negocio;
  useEffect(() => {
    setCopiado(false);
    const canvas = dibujarParte(
      deHoy ? armarParte(deHoy, { negocio: negocioDelParte, dia: diaDelParte }) : null);
    if (!canvas) { setImagen(null); return undefined; }

    let vivo = true;
    let creada = null;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      if (!vivo) return URL.revokeObjectURL(url);
      creada = url;
      setImagen({ url, blob });
    }, 'image/png');

    return () => { vivo = false; if (creada) URL.revokeObjectURL(creada); };
  }, [deHoy, negocioDelParte, diaDelParte]);

  if (error) return <div className="aviso malo">{error}</div>;
  if (!datos) return <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>;

  const rutas = compararRutas(datos.centros || []);
  const sinRuta = datos.sin_centro || {};

  // El total del negocio, para poder decir si una ruta va por encima o por
  // debajo de la casa. Una nota sola no dice contra qué.
  const conProduccion = rutas.filter(r => r.lectura);
  const quietas = rutas.filter(r => !r.lectura);

  const T = conProduccion.reduce((a, r) => ({
    litros: a.litros + r.lectura.litros, kilos: a.kilos + r.lectura.kilos,
    esperados: a.esperados + (Number(r.kilos_esperados) || 0),
    medibles: a.medibles + (Number(r.kilos_medibles) || 0),
  }), { litros: 0, kilos: 0, esperados: 0, medibles: 0 });
  const cumpleCasa = T.esperados > 0
    ? Math.round((T.medibles / T.esperados) * 1000) / 10 : null;
  const porTinaCasa = T.litros > 0
    ? Math.round((T.kilos / (T.litros / LITROS_POR_TINA)) * 100) / 100 : null;

  const gastoDe = (id) => insumos.filter(x => x.centro_id === id);
  const costoInsumosDe = (id) => gastoDe(id).reduce((s, x) => s + (x.costo || 0), 0);

  return (
    <>
      <div className="encabezado">
        <h1>Rutas</h1>
        <p>Cuál está rindiendo, cuánto produce y qué se le va en insumos</p>
      </div>

      {/* EL PARTE DEL DÍA.
          
          Quien registra la producción no es quien manda en el negocio: el
          muchacho anota y después le pasa los promedios al patrón por
          teléfono, copiando los números de esta pantalla a mano. Un número
          copiado a mano todos los días, tarde o temprano, sale distinto del que
          está en el programa — y el que queda mal es el muchacho.
          
          Va ARRIBA porque es lo último que se hace en el día y lo primero que
          hay que encontrar. Y NO SE MANDA SOLO: se abre WhatsApp con el parte
          escrito y él le da a enviar. Un parte con una cifra equivocada, salido
          solo, no se puede recoger.
          
          EL BLOQUE SE ENSEÑA AUNQUE NO HAYA PARTE. Al principio lo escondía
          cuando el día no tenía cuajadas, y eso lo hacía invisible justo cuando
          alguien entraba a buscarlo: se abre Rutas, no hay nada, y se concluye
          que la función no existe. Una herramienta que solo aparece cuando ya
          no hace falta buscarla no se encuentra nunca. Sin cuajadas se dice qué
          falta para tenerlo. */}
      {deHoy && (
        <div className="bloque">
          <div className="cuerpo" style={{ display: 'flex', gap: 10,
                                           alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div className="recio" style={{ fontSize: 15 }}>
                {/* El título DICE DE QUÉ DÍA ES. Sin eso, cambiando las
                    fechas de abajo se manda el parte de otro día sin que nada
                    lo delate. */}
                📲 El parte {esHoy ? 'de hoy' : `del ${diaLargo(diaDelParte)}`}
                {parte ? ' está listo' : ''}
              </div>
              <div style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                {parte
                  ? 'Los promedios por ruta, para mandárselos al patrón'
                  : (esHoy
                      ? 'Todavía no hay cuajadas registradas hoy. Ciérrelas en Producción y aquí queda listo el parte.'
                      : `No hay cuajadas registradas el ${diaLargo(diaDelParte)}. ` +
                        'Ponga arriba un solo día con producción, o vuelva a hoy.')}
              </div>
            </div>
            {parte && (
              <button type="button" className="boton suave"
                      onClick={() => setVerParte(v => !v)}>
                {verParte ? 'Ocultar' : 'Ver cómo queda'}
              </button>
            )}
            {/* COPIAR LA IMAGEN, que es lo que el cliente pidió.
                
                Un enlace de WhatsApp solo puede llevar texto —no existe forma
                de abrir el chat con una foto ya puesta—, así que son dos
                toques: copiar aquí y pegar allá con Ctrl+V. El botón lo dice
                para que nadie se quede esperando que salga sola. */}
            {parte && imagen ? (
              <button type="button" className="boton"
                      onClick={async () => {
                        const ok = await copiarImagen(imagen.blob);
                        setCopiado(ok ? 'si' : 'no');
                      }}>
                🖼️ Copiar la imagen
              </button>
            ) : (
              <span className="boton suave" style={{ opacity: .5, cursor: 'default' }}>
                🖼️ Copiar la imagen
              </span>
            )}
            {/* Descargar SIEMPRE a la vista, no escondida detrás de que
                copiar falle. Copiar depende de un permiso que algunos
                navegadores niegan sin avisar; descargar funciona en todos. El
                que se queda sin poder mandar el parte no va a ponerse a buscar
                por qué. */}
            {parte && imagen ? (
              <a className="boton suave" href={imagen.url}
                 download={nombreDelArchivo(diaDelParte)}>
                ⬇️ Descargarla
              </a>
            ) : (
              <span className="boton suave" style={{ opacity: .5, cursor: 'default' }}>
                ⬇️ Descargarla
              </span>
            )}
            {parte ? (
              <a className="boton suave"
                 href={enlaceDeWhatsApp((ajustes || {}).whatsapp_parte, '')}
                 target="_blank" rel="noopener noreferrer">
                💬 Abrir el chat
              </a>
            ) : (
              <span className="boton suave" style={{ opacity: .5, cursor: 'default' }}>
                💬 Abrir el chat
              </span>
            )}
          </div>

          {/* Qué hacer ahora. Sin este renglón, quien copia la imagen se queda
              mirando la pantalla esperando que pase algo. */}
          {copiado === 'si' && (
            <div className="cuerpo" style={{ paddingTop: 0 }}>
              <div className="aviso bueno" style={{ margin: 0 }}>
                <strong>Copiada.</strong> Abra el chat de Gabriel y pegue con{' '}
                <strong>Ctrl + V</strong>. Después le da a enviar.
              </div>
            </div>
          )}
          {copiado === 'no' && imagen && (
            <div className="cuerpo" style={{ paddingTop: 0 }}>
              <div className="aviso ojo" style={{ margin: 0 }}>
                Este navegador no deja copiar imágenes. Use{' '}
                <strong>⬇️ Descargarla</strong> y arrastre el archivo al chat
                de WhatsApp.
              </div>
            </div>
          )}

          {verParte && (
            <div className="cuerpo" style={{ paddingTop: 0 }}>
              {/* Se enseña la IMAGEN, no el texto: es lo que va a recibir
                  Gabriel, y hay que poder revisarla antes de mandarla. Un
                  parte con una cifra equivocada, una vez enviado, no se
                  recoge. */}
              {imagen && (
                <img src={imagen.url} alt={`Parte del ${diaLargo(diaDelParte)}`}
                     style={{ maxWidth: '100%', borderRadius: 10,
                              border: '1px solid var(--raya)' }} />
              )}
              <div style={{ marginTop: 10, fontSize: 13 }}>
                <a href={imagen ? imagen.url : undefined}
                   download={nombreDelArchivo(diaDelParte)}>⬇️ Descargar la imagen</a>
                {'  ·  '}
                <a href={enlaceDeWhatsApp((ajustes || {}).whatsapp_parte, parte)}
                   target="_blank" rel="noopener noreferrer">
                  Mandarlo como texto
                </a>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bloque">
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
        </div>
      </div>

      {conProduccion.length === 0 && quietas.length === 0 ? (
        <div className="bloque">
          <div className="vacio">
            No hay cuajadas con ruta anotada en este periodo. Las rutas se crean
            en 🧀 Producción, y al cuajar se escoge en cuál se hizo.
          </div>
        </div>
      ) : (
        <>
          <div className="rejilla">
            <div className="dato" style={{ '--color': 'var(--acento-vivo)' }}>
              <div className="rotulo">🧀 Producido</div>
              <div className="valor cifra">{peso(T.kilos, 'kg')}</div>
              <div className="pie">
                con {peso(T.litros, 'L')} · {Math.round(T.litros / LITROS_POR_TINA)} tinas
              </div>
            </div>
            {porTinaCasa && (
              <div className="dato" style={{ '--color': 'var(--leche)' }}>
                <div className="rotulo">⚖️ Promedio de la casa</div>
                <div className="valor cifra">{promedio(porTinaCasa)}</div>
                <div className="pie">kilos por tina, entre todas las rutas</div>
              </div>
            )}
            {cumpleCasa !== null && (
              <div className="dato" style={{ '--color': calificar(cumpleCasa).color }}>
                <div className="rotulo">🎯 Cumplimiento</div>
                <div className="valor cifra">{conComa(cumpleCasa)}%</div>
                <div className="pie">
                  de lo que la leche debía dar · {calificar(cumpleCasa).nombre}
                </div>
              </div>
            )}
          </div>

          {/* LA LECHE QUE ENTRÓ POR RUTA.
              
              Va ANTES del rendimiento porque es la pregunta anterior: primero
              cuánta leche recibió cada ruta, después qué hizo con ella.
              
              Y son dos cosas distintas: una es lo que RECIBIÓ —de los recibos—
              y otra lo que se CUAJÓ. No tienen por qué ser iguales, y la
              diferencia es justamente lo que hay que poder mirar. */}
          <div className="bloque">
            <h2>🥛 La leche que entró, por ruta</h2>
            <div className="cuerpo">
              {!leche || leche.rutas.every(r => !r.litros) ? (
                <div className="vacio">
                  No hay entregas anotadas en ese periodo.
                </div>
              ) : (
                <>
                  <div className="tabla-caja">
                    <table className="datos">
                      <thead>
                        <tr>
                          <th>Ruta</th>
                          <th style={{ textAlign: 'right' }}>Litros</th>
                          <th style={{ textAlign: 'right' }}>Fincas</th>
                          <th style={{ textAlign: 'right' }}>Entregas</th>
                          <th style={{ textAlign: 'right' }}>Se le pagó</th>
                          <th style={{ textAlign: 'right' }}>Gastos</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leche.rutas.filter(r => r.litros > 0).map(r => {
                          // El gasto del periodo: el fijo por cada DÍA que hubo
                          // entregas. Multiplicarlo por los días y no por uno es
                          // lo que hace que la cifra sirva para un rango.
                          //
                          // El recargo por litro de búfala YA NO SE SUMA: era el
                          // sobreprecio de esa leche, y el programa ya la compra
                          // a su propio precio. Ver el aviso de más abajo.
                          const fijo = (r.gasto_fijo || 0) * (r.dias || 0);
                          return (
                          <tr key={r.id}>
                            <td className="recio">{r.nombre}</td>
                            <td className="num recio">{peso(r.litros, 'L')}
                              {r.litros_bufala > 0 && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                              color: 'var(--tinta-tenue)' }}>
                                  🐃 {peso(r.litros_bufala, 'L')}
                                </div>
                              )}
                            </td>
                            <td className="num">{r.fincas}</td>
                            <td className="num">{r.entregas}</td>
                            <td className="num">{plata(r.plata)}</td>
                            <td className="num">
                              {fijo > 0 ? plata(fijo) : '—'}
                              {fijo > 0 && r.dias > 1 && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                              color: 'var(--tinta-tenue)' }}>
                                  {plata(r.gasto_fijo)} × {r.dias} días
                                </div>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* DÓNDE SE PACTA EL GASTO DE CADA RUTA.
                      
                      «El gasto fijo es el que tiene cada ruta: día del
                      trabajador, gasolina, etc.»
                      
                      Se pone aquí y no en otra pantalla porque esta es la de las
                      rutas. Un ajuste escondido en otro sitio es un ajuste que
                      nadie pone, y entonces la cifra sale en cero para siempre.
                      
                      YA NO SE PIDE EL RECARGO POR LITRO DE BÚFALA. Ver el aviso
                      que va debajo del formulario: se cobraba dos veces. */}
                  <div style={{ marginTop: 14 }}>
                    {!gastos ? (
                      <button className="boton chico suave"
                              onClick={() => setGastos(
                                Object.fromEntries(leche.rutas.map(r => [r.id, {
                                  fijo: String(Math.round(r.gasto_fijo || 0)),
                                  cruda: !!r.vende_cruda,
                                }])))}>
                        ⚙️ Pactar los gastos de cada ruta
                      </button>
                    ) : (
                      <div style={{ border: '1px solid var(--raya)', borderRadius: 10,
                                    padding: '14px 16px' }}>
                        <div style={{ fontSize: 14, marginBottom: 12 }}>
                          <strong>Lo que cuesta cada ruta al día:</strong> el día
                          del trabajador, la gasolina y lo demás de salir a
                          recogerla. Se cuenta por cada día que hubo entregas.
                        </div>
                        {/* EL AVISO QUE FALTABA.
                            
                            Gabriel tecleó aquí la cifra de su hoja de Excel, que
                            allá SÍ lleva el sobreprecio de la búfala —porque en
                            su hoja toda la leche se valora al precio de la
                            vaca—. Aquí no hace falta: cada cuajada guarda la
                            búfala a su propio precio. Copiada tal cual, LA YERA
                            salía perdiendo $27.853 cuando gana. */}
                        <div className="aviso ojo" style={{ marginBottom: 12 }}>
                          <strong>Aquí NO va el sobreprecio de la leche de
                          búfala.</strong> El programa ya la compra a su precio
                          —lo que se paga de más está dentro de la columna
                          «Leche»— y sumándolo otra vez la ruta con búfala sale
                          perdiendo sin estarlo. Si su hoja aparte dice «25.000
                          más 1.000 por litro de búfala», aquí van los 25.000.
                          {' '}Ese sobreprecio <strong>sí se le está cobrando</strong>,
                          y ahora se ve: en la tabla de arriba sale con un 🐃
                          debajo de la columna «Leche».
                          <div style={{ marginTop: 8 }}>
                            Y marque <strong>una sola ruta</strong> como la que
                            vende leche cruda: es la única a la que se le
                            descuentan esos litros al hacer el queso.
                          </div>
                        </div>
                        {leche.rutas.map(r => (
                          <div key={r.id} style={{ display: 'flex', alignItems: 'center',
                                                   gap: 10, flexWrap: 'wrap',
                                                   padding: '7px 0',
                                                   borderTop: '1px solid var(--raya)' }}>
                            <strong style={{ minWidth: 140 }}>{r.nombre}</strong>
                            <label style={{ fontSize: 13 }}>
                              Fijo al día{' '}
                              <input type="number" step="100" min="0" style={{ width: 110 }}
                                     value={gastos[r.id]?.fijo ?? ''}
                                     onChange={e => setGastos({ ...gastos,
                                       [r.id]: { ...gastos[r.id], fijo: e.target.value } })} />
                            </label>
                            {/* DE CUÁL RUTA SALE LA LECHE QUE SE VENDE CRUDA.
                                
                                La venta no guarda de qué tanque salió, así que
                                al proponer los litros de una cuajada el programa
                                se la restaba a CUALQUIER ruta: a LA YERA, que
                                recibió 383 L, le proponía 12. Marcada una, las
                                demás proponen sus litros completos. */}
                            <label style={{ fontSize: 13, display: 'flex',
                                            alignItems: 'center', gap: 6 }}>
                              <input type="checkbox"
                                     checked={!!gastos[r.id]?.cruda}
                                     onChange={e => setGastos({ ...gastos,
                                       [r.id]: { ...gastos[r.id], cruda: e.target.checked } })} />
                              🥛 De aquí sale la leche cruda
                            </label>
                          </div>
                        ))}
                        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                          <button className="boton chico suave"
                                  onClick={() => setGastos(null)}>Cancelar</button>
                          <button className="boton chico" disabled={guardandoGastos}
                                  onClick={async () => {
                                    setGuardandoGastos(true);
                                    try {
                                      for (const r of leche.rutas) {
                                        const g = gastos[r.id] || {};
                                        await axios.put(`/api/centros/${r.id}`, {
                                          nombre: r.nombre, activo: 1,
                                          gasto_fijo: parseFloat(g.fijo) || 0,
                                          vende_cruda: g.cruda ? 1 : 0,
                                          // A cero: el recargo por litro de
                                          // búfala se cobraba dos veces y ya no
                                          // se usa. Dejarlo guardado con un
                                          // valor sería munición para que
                                          // alguien lo vuelva a sumar.
                                          gasto_bufala: 0,
                                        });
                                      }
                                      setGastos(null);
                                      cargar();
                                    } catch (err) {
                                      alert(err.response?.data?.error || 'No se pudo guardar');
                                    }
                                    setGuardandoGastos(false);
                                  }}>
                            {guardandoGastos ? 'Guardando…' : '💾 Guardar los gastos'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* La que llegó SIN ruta puesta. Esconderla haría que los
                      totales por ruta no sumaran lo mismo que la leche del
                      periodo, y ese descuadre es el que hace desconfiar de una
                      pantalla entera. */}
                  {leche.sin_ruta && leche.sin_ruta.entregas > 0 && (
                    <div className="aviso ojo" style={{ marginTop: 12 }}>
                      <strong>{peso(leche.sin_ruta.litros, 'L')} en{' '}
                      {leche.sin_ruta.entregas} entregas sin ruta anotada.</strong>{' '}
                      Esos litros no aparecen en ninguna fila de arriba. En 🚜 Fincas
                      hay un botón para ponerle la ruta a cada finca.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── LA UTILIDAD DE CADA RUTA ────────────────────────────────────
              «Los promedios por ruta con la utilidad del día de cada ruta, los
              gastos y la ganancia.»

              A QUÉ PRECIO SE VALORAN LOS KILOS. «La leche depende del valor del
              queso: si baja el queso baja la leche. Ahora está a 19.500 pero eso
              varía.» Por eso no hay un precio escrito por ningún lado: se toma
              el precio REAL al que se vendió ese producto en el mismo periodo.
              Se mueve solo cuando el mercado se mueve.

              Y se dice a qué precio se valoró. Una utilidad es una cifra que se
              discute; si la pantalla no puede decir de dónde salió el precio, la
              discusión no tiene dónde agarrarse. */}
          {utilidad && utilidad.rutas.length > 0 && (
            <div className="bloque">
              <h2>💰 Lo que deja cada ruta</h2>
              <div className="cuerpo">
                {/* El `id` es para el manual: el guion de las fotos recorta
                    justo esta tabla. Sin él tendría que adivinar cuál de las
                    tablas de la pantalla es, y el día que se agregue otra el
                    manual saldría enseñando la que no es. */}
                <div className="tabla-caja" id="utilidad-rutas">
                  <table className="datos">
                    <thead>
                      {/* Las columnas y las palabras son las de la hoja que
                          Gabriel ya lleva: PROMEDIO, UTILIDAD, GASTOS,
                          GANANCIA. Yo llamaba «utilidad» a lo que él llama
                          ganancia, y él estaba comparando mi columna con la
                          suya sin que ninguno supiera que eran cosas
                          distintas. */}
                      <tr>
                        <th>Ruta</th>
                        <th style={{ textAlign: 'right' }}>Litros</th>
                        <th style={{ textAlign: 'right' }}>Kilos</th>
                        {/* EL PROMEDIO DE GABRIEL SON KILOS POR TINA.
                            
                            Lo puse al revés. Su columna PROMEDIO decía 6,486707
                            en RIO PIEDRA y yo enseñaba 6,17 llamándolo igual:
                            los litros por kilo. Preguntó «¿esto a qué se
                            refiere?» y tenía toda la razón.
                            
                            Comparadas las seis rutas contra su hoja, cinco
                            coinciden al tercer decimal con los KILOS POR TINA.
                            Ésa es su medida, así que ésa va grande; los litros
                            por kilo quedan debajo, que también sirven y son la
                            misma cuenta al revés. */}
                        <th style={{ textAlign: 'right' }}>Promedio<br />
                          <span style={{ fontWeight: 400, fontSize: 11 }}>kg por tina</span>
                        </th>
                        <th style={{ textAlign: 'right' }}>Vale</th>
                        <th style={{ textAlign: 'right' }}>Leche</th>
                        <th style={{ textAlign: 'right' }}>Utilidad</th>
                        <th style={{ textAlign: 'right' }}>Gastos</th>
                        <th style={{ textAlign: 'right' }}>Ganancia</th>
                        <th style={{ textAlign: 'right' }}>Por litro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...utilidad.rutas]
                        .sort((a, b) => (b.por_litro || 0) - (a.por_litro || 0))
                        .map(r => {
                        // Los gastos de la fila son TODO lo que no es la leche:
                        // recogerla, los insumos y la mano de obra. Enseñar solo
                        // la recogida dejaría una resta que no cuadra a la vista.
                        return (
                          <tr key={r.id}>
                            <td className="recio">{r.nombre}
                              <div style={{ fontSize: 11, fontWeight: 400,
                                            color: 'var(--tinta-tenue)' }}>
                                {r.dias} {r.dias === 1 ? 'día' : 'días'}
                                {r.litros_bufala > 0 && ` · 🐃 ${peso(r.litros_bufala, 'L')}`}
                              </div>
                            </td>
                            <td className="num">{peso(r.litros, 'L')}</td>
                            <td className="num">{peso(r.kilos, 'kg')}</td>
                            {/* Litros por kilo. Es la columna con la que él
                                abre su hoja, y hasta ahora vivía en la otra
                                tabla con otro nombre —«kg por tina»—, que es la
                                misma cuenta al revés. */}
                            <td className="num recio">
                              {/* Del servidor, no de dividir 40 entre el
                                  promedio: ése ya viene redondeado a dos
                                  decimales y la división corre el tercero. */}
                              {r.por_tina === null || r.por_tina === undefined ? '—'
                                : conComa(Math.round(r.por_tina * 100) / 100)}
                              {r.promedio > 0 && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                              color: 'var(--tinta-tenue)' }}>
                                  {conComa(r.promedio)} L/kg
                                </div>
                              )}
                            </td>
                            <td className="num">{plata(r.valor)}</td>
                            <td className="num">
                              {plata(r.costo_leche)}
                              {r.leche_por_litro && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                              color: 'var(--tinta-tenue)' }}>
                                  {plata(r.leche_por_litro)}/L
                                </div>
                              )}
                              {/* EL GASTO DE LA BÚFALA, A LA VISTA.
                                  
                                  «Ya está agregado el gasto de la leche de
                                  búfala. ¿No existe la posibilidad de que ese
                                  gasto se vea reflejado ahí?»
                                  
                                  Existe y se está pagando: está DENTRO de esta
                                  columna, porque el programa compra la búfala a
                                  su precio en vez de valorarla como vaca y
                                  sumar la diferencia aparte. Como no se veía,
                                  parecía que no se cobraba — y de ahí salió el
                                  impulso de volver a sumarlo a los gastos, que
                                  es cobrarlo dos veces. */}
                              {r.sobreprecio_bufala && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                              color: 'var(--acento)' }}>
                                  🐃 {plata(r.sobreprecio_bufala.total)} de más
                                  <div style={{ color: 'var(--tinta-tenue)' }}>
                                    {peso(r.sobreprecio_bufala.litros, 'L')} ×{' '}
                                    {plata(r.sobreprecio_bufala.por_litro)}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="num">{plata(r.utilidad)}</td>
                            <td className="num">
                              {plata(r.gastos)}
                              {r.recogida > 0 && r.gastos !== r.recogida && (
                                <div style={{ fontSize: 11, fontWeight: 400,
                                              color: 'var(--tinta-tenue)' }}>
                                  {plata(r.recogida)} de ruta
                                </div>
                              )}
                            </td>
                            {/* Una ruta que pierde plata se escribe en rojo. Un
                                número negativo entre otros positivos se lee por
                                encima como uno más. */}
                            <td className="num recio"
                                style={{ color: r.ganancia >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
                              {plata(r.ganancia)}
                              {r.margen !== null && (
                                <div style={{ fontSize: 11, fontWeight: 400 }}>
                                  {conComa(r.margen)}% de lo vendido
                                </div>
                              )}
                            </td>
                            {/* POR LITRO es la columna por la que se ordena.
                                En pesos totales gana siempre la ruta más grande
                                aunque sea la que peor deja, y ese ranking hace
                                tomar la decisión al revés. */}
                            <td className="num recio">
                              {r.por_litro === null ? '—' : plata(r.por_litro)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* DE DÓNDE SALIÓ EL PRECIO. Va debajo de la tabla y no
                    escondido: es el supuesto del que cuelga toda la columna de
                    la ganancia. */}
                <div className="aviso" id="utilidad-precios" style={{ marginTop: 12 }}>
                  <strong>Los kilos se valoraron así:</strong>{' '}
                  {(utilidad.precios || []).map((p, n) => (
                    <span key={n}>
                      {n > 0 ? ' · ' : ''}
                      {p.nombre} a {plata(p.precio)}
                      {p.de === 'referencia'
                        ? (p.rigieron && p.rigieron.length > 1
                            ? ` (fijado: ${p.rigieron.map(x => `${plata(x.precio)} desde el ${fecha(x.desde)}`).join(', ')})`
                            : ` (el precio fijado, desde el ${fecha(p.desde)})`)
                        : p.de === 'ventas'
                        ? ` (lo que se vendió de verdad en el periodo: ${peso(p.kilos, 'kg')} en ${p.veces} ${p.veces === 1 ? 'venta' : 'ventas'})`
                        : p.de === 'escrito' ? ' (el que usted escribió)'
                        : ' (el de la ficha — en este periodo no se vendió nada de esto)'}
                    </span>
                  ))}
                  <div style={{ marginTop: 8 }}>
                    Los kilos de una ruta se revuelven en la cámara con los de
                    las demás, así que no hay forma de saber a qué precio se
                    vendió el queso de una ruta en particular. Valorarlos al
                    mismo precio sirve para <strong>comparar las rutas entre
                    sí</strong>, que es para lo que se hizo esta tabla.
                  </div>
                </div>

                {/* FIJAR EL PRECIO DE LA TEMPORADA.
                    
                    «Tener en cuenta que los valores tanto de la leche como del
                    queso VARÍAN. Hasta el 31 de agosto el queso 19.200; a partir
                    del 1ro de este mes, 19.700.»
                    
                    Valorar al promedio de las ventas se mueve solo con el
                    mercado, que suena mejor — pero contra un promedio que cambia
                    cada semana, dos meses no se pueden poner uno al lado del
                    otro. Él fija un precio por temporada y con ÉSE compara. */}
                <div style={{ marginTop: 12 }}>
                  {!fijando ? (
                    <button className="boton chico suave"
                            onClick={() => setFijando({
                              producto_id: (productos.find(p => p.activo !== 0) || {}).id || '',
                              desde: hoy().slice(0, 8) + '01', precio: '' })}>
                      💲 Fijar el precio de la temporada
                    </button>
                  ) : (
                    <div style={{ border: '1px solid var(--raya)', borderRadius: 10,
                                  padding: '14px 16px' }}>
                      <div style={{ fontSize: 14, marginBottom: 12 }}>
                        <strong>El precio con el que se valoran los kilos.</strong>{' '}
                        Rige <em>desde</em> esa fecha en adelante, hasta que se
                        fije otro. Un rango que cruce un cambio valora cada día
                        con el precio que regía ese día.
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap',
                                    alignItems: 'flex-end' }}>
                        <label className="campo" style={{ minWidth: 200 }}>
                          <span>Producto</span>
                          <select value={fijando.producto_id}
                                  onChange={e => setFijando({ ...fijando,
                                    producto_id: e.target.value })}>
                            {productos.map(p => (
                              <option key={p.id} value={p.id}>{p.nombre}</option>
                            ))}
                          </select>
                        </label>
                        <label className="campo">
                          <span>Desde</span>
                          <input type="date" value={fijando.desde}
                                 onChange={e => setFijando({ ...fijando,
                                   desde: e.target.value })} />
                        </label>
                        <label className="campo">
                          <span>Precio por kilo</span>
                          <input type="number" step="1" min="0" className="peso"
                                 value={fijando.precio}
                                 onChange={e => setFijando({ ...fijando,
                                   precio: e.target.value })} />
                        </label>
                        <button className="boton chico" disabled={guardandoPrecio}
                                onClick={async () => {
                                  setGuardandoPrecio(true);
                                  try {
                                    await axios.post('/api/precios-referencia', {
                                      producto_id: parseInt(fijando.producto_id),
                                      desde: fijando.desde,
                                      precio: parseFloat(fijando.precio) || 0,
                                    });
                                    setFijando(null);
                                    cargar();
                                  } catch (err) {
                                    alert(err.response?.data?.error || 'No se pudo guardar');
                                  }
                                  setGuardandoPrecio(false);
                                }}>
                          {guardandoPrecio ? 'Guardando…' : '💾 Guardar'}
                        </button>
                        <button className="boton chico suave"
                                onClick={() => setFijando(null)}>Cancelar</button>
                      </div>

                      {precios.length > 0 && (
                        <div style={{ marginTop: 14 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                            Los que están puestos
                          </div>
                          {precios.map(p => (
                            <div key={p.id} style={{ display: 'flex', alignItems: 'center',
                                                     gap: 10, fontSize: 13,
                                                     padding: '5px 0',
                                                     borderTop: '1px solid var(--raya)' }}>
                              <span style={{ minWidth: 170 }}>{p.producto}</span>
                              <span style={{ minWidth: 150 }}>
                                desde el {fecha(p.desde)}
                              </span>
                              <strong>{plata(p.precio)}</strong>
                              <button className="boton chico suave"
                                      style={{ marginLeft: 'auto' }}
                                      onClick={async () => {
                                        if (!window.confirm(
                                          `¿Quitar el precio de ${p.producto} desde el ` +
                                          `${fecha(p.desde)}?\n\nLos días que valoraba ` +
                                          `pasan al precio anterior, o al promedio de las ` +
                                          `ventas si no hay ninguno.`)) return;
                                        await axios.delete(`/api/precios-referencia/${p.id}`);
                                        cargar();
                                      }}>
                                Quitar
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* EL PODIO. Ordenado por cumplimiento, no por kilos por tina: la
              ruta a la que le toca más búfala saca más kilos por tina sin
              hacer nada mejor. */}
          <div className="bloque">
            <h2>🏁 Cómo va cada ruta</h2>
            <div className="cuerpo">
              <div style={{ display: 'grid', gap: 12,
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                                {conProduccion.length === 0 && (
                  <div className="aviso ojo" style={{ gridColumn: '1 / -1' }}>
                    Ninguna ruta cuajó en este periodo. Pruebe cambiando las
                    fechas de arriba.
                  </div>
                )}
                {conProduccion.map((r, i) => {
                  const L = r.lectura;
                  const c = calificar(L.cumple);
                  const gasto = costoInsumosDe(r.id);
                  return (
                    <div key={r.id} style={{
                      border: '1px solid var(--linea)', borderLeft: `5px solid ${c.color}`,
                      borderRadius: 10, padding: '14px 16px', background: 'var(--tarjeta)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        {/* La medalla solo si de verdad se pudo medir. Un 🥇 a
                            una ruta sin cumplimiento sería un premio sorteado. */}
                        <span style={{ fontSize: 20 }}>
                          {L.cumple !== null && i < 3 ? MEDALLA[i] : '🏭'}
                        </span>
                        <div className="recio" style={{ fontSize: 17 }}>{r.nombre}</div>
                      </div>

                      {/* EL PROMEDIO VA GRANDE, el cumplimiento al lado.
                          
                          Es el número que pidió el quesero y el que se habla en
                          la finca: kilos de queso entre tinas de leche. Estaba
                          en letra chica dentro de la tabla de abajo, y arriba
                          iba un porcentaje que él nunca pidió — así que la
                          pantalla contestaba otra pregunta.
                          
                          El cumplimiento se queda, en pequeño y con su color:
                          es el que decide el puesto, y quitarlo dejaría la
                          medalla sin explicación. */}
                      <div style={{ display: 'flex', alignItems: 'baseline',
                                    gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                        <div className="cifra recio" style={{ fontSize: 34 }}>
                          {promedio(L.porTina)}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
                          kg por tina
                        </div>
                        <div style={{ marginLeft: 'auto', fontSize: 13, fontWeight: 700,
                                      color: c.color }}>
                          {L.cumple !== null ? conComa(L.cumple) + '% ' + c.nombre : 'sin medir'}
                        </div>
                      </div>

                      {/* La barra. Empieza en el 80% para que las diferencias
                          se vean: desde cero todas saldrían casi llenas. */}
                      <div style={{ height: 8, borderRadius: 4, marginTop: 6,
                                    background: 'var(--hondo)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: llenado(L.cumple) + '%',
                                      background: c.color, transition: 'width .3s' }} />
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                        de lo que la leche que entró debía dar
                      </div>

                      <table style={{ width: '100%', marginTop: 12, fontSize: 13 }}>
                        <tbody>
                          <tr>
                            <td style={{ color: 'var(--tinta-tenue)' }}>Produjo</td>
                            <td className="num">{peso(L.kilos, 'kg')}</td>
                          </tr>
                          <tr>
                            <td style={{ color: 'var(--tinta-tenue)' }}>Tinas</td>
                            <td className="num">{conComa(L.tinas)}</td>
                          </tr>
                          {r.costo_kilo > 0 && (
                            <tr>
                              <td style={{ color: 'var(--tinta-tenue)' }}>Costo del kilo</td>
                              <td className="num">{plata(r.costo_kilo)}</td>
                            </tr>
                          )}
                          {gasto > 0 && (
                            <tr>
                              <td style={{ color: 'var(--tinta-tenue)' }}>Insumos</td>
                              <td className="num">{plata(gasto)}</td>
                            </tr>
                          )}
                          {L.parteBufala > 0 && (
                            <tr>
                              <td style={{ color: 'var(--tinta-tenue)' }}>🐃 Búfala</td>
                              <td className="num">{L.parteBufala}% de la leche</td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* EL DÍA A DÍA. El promedio del periodo dice cómo va
                          la ruta; esto dice qué día se torció. Un 95% en el mes
                          puede ser 29 días clavados y uno malo, o 30 días
                          flojos — dos problemas distintos con causas distintas. */}
                      {(() => {
                        const dias = diasDeRuta(porDia, r.id);
                        if (dias.length === 0) return null;
                        const v = variacion(dias);
                        const abierto = abierta === r.id;
                        return (
                          <>
                            {v && (
                              <div style={{ fontSize: 12, marginTop: 8,
                                            color: 'var(--tinta-tenue)' }}>
                                En {v.dias} días se movió entre{' '}
                                <strong style={{ color: calificar(v.peor).color }}>
                                  {conComa(v.peor)}%
                                </strong>{' '}y{' '}
                                <strong style={{ color: calificar(v.mejor).color }}>
                                  {conComa(v.mejor)}%
                                </strong>
                                {/* La horquilla dice si el proceso anda o si va
                                    a los tumbos. Dos rutas con el mismo
                                    promedio no son iguales. */}
                                {v.horquilla >= 8 && (
                                  <div style={{ color: 'var(--ojo)', marginTop: 2 }}>
                                    Se mueve {conComa(v.horquilla)} puntos. El día flojo fue
                                    el {fecha(v.peorDia.fecha)}.
                                  </div>
                                )}
                              </div>
                            )}
                            <button className="boton chico suave"
                                    style={{ marginTop: 8, width: '100%' }}
                                    onClick={() => setAbierta(abierto ? null : r.id)}>
                              {abierto ? 'Ocultar los días'
                                : `Ver los ${dias.length} días`}
                            </button>
                            {abierto && (
                              <table style={{ width: '100%', marginTop: 8, fontSize: 12 }}>
                                <thead>
                                  <tr style={{ color: 'var(--tinta-tenue)' }}>
                                    <th style={{ textAlign: 'left', fontWeight: 400 }}>Día</th>
                                    <th style={{ textAlign: 'right', fontWeight: 400 }}>Tinas</th>
                                    <th style={{ textAlign: 'right', fontWeight: 400 }}>Kilos</th>
                                    <th style={{ textAlign: 'right', fontWeight: 400 }}>Por tina</th>
                                    <th style={{ textAlign: 'right', fontWeight: 400 }}>Nota</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {dias.map(d => (
                                    <tr key={d.fecha}>
                                      <td>{fecha(d.fecha)}</td>
                                      <td className="num">{conComa(d.tinas)}</td>
                                      <td className="num">{d.kilos}</td>
                                      <td className="num recio">{promedio(d.porTina)}</td>
                                      <td className="num recio"
                                          style={{ color: calificar(d.cumple).color }}>
                                        {d.cumple !== null ? conComa(d.cumple) + '%' : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </>
                        );
                      })()}

                      {/* Lo que quedó fuera de la nota. Un porcentaje que tapa
                          la mitad de la producción hay que decirlo. */}
                      {L.sinMedir > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--ojo)', marginTop: 8 }}>
                          ⚠️ {L.sinMedir} de {L.cuajadas} cuajadas no entran en la
                          nota: les falta el rendimiento pactado de alguna leche.
                        </div>
                      )}
                    </div>
                  );
                })}

                {quietas.map(r => (
                  <div key={r.id} style={{
                    border: '1px dashed var(--linea)', borderRadius: 10,
                    padding: '14px 16px', background: 'var(--hondo)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                      <span style={{ fontSize: 20, opacity: .5 }}>🏭</span>
                      <div className="recio" style={{ fontSize: 17,
                                                      color: 'var(--tinta-tenue)' }}>
                        {r.nombre}
                      </div>
                    </div>
                    <p style={{ margin: '10px 0 0', fontSize: 13,
                                color: 'var(--tinta-tenue)' }}>
                      No hizo ninguna cuajada en este periodo. No tiene nota
                      porque no hay nada que medir, no porque le haya ido mal.
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Lo que se le va a cada ruta en sal y cuajo. Es el otro lado del
              costo: dos rutas que rinden igual pueden gastar muy distinto. */}
          {insumos.length > 0 && (
            <div className="bloque">
              <h2>📦 Qué se le va a cada ruta</h2>
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Ruta</th>
                      <th>Insumo</th>
                      <th style={{ textAlign: 'right' }}>Gastó</th>
                      <th style={{ textAlign: 'right' }}>Costó</th>
                      <th style={{ textAlign: 'right' }}>Por kilo de queso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rutas.map(r => gastoDe(r.id).map((g, n) => (
                      <tr key={`${r.id}-${g.insumo_id}`}>
                        <td className="recio">{n === 0 ? r.nombre : ''}</td>
                        <td>{g.insumo}</td>
                        <td className="num">{peso(g.usado, g.unidad)}</td>
                        <td className="num">{plata(g.costo)}</td>
                        {/* Por kilo, que es lo comparable. En total, la ruta
                            que más produce siempre gasta más y no dice nada. */}
                        <td className="num recio">
                          {r.lectura.kilos > 0 ? plata(g.costo / r.lectura.kilos) : '—'}
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Lo que tiene cada ruta guardado. Es la pregunta del día siguiente:
              ¿alcanza para cuajar mañana? */}
          {enBodega.length > 0 && (
            <div className="bloque">
              <h2>🧂 Qué le queda a cada ruta</h2>
              <div className="tabla-caja">
                <table className="datos">
                  <thead>
                    <tr>
                      <th>Ruta</th>
                      <th>Insumo</th>
                      <th style={{ textAlign: 'right' }}>Le llegó</th>
                      <th style={{ textAlign: 'right' }}>Gastó</th>
                      <th style={{ textAlign: 'right' }}>Queda</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enBodega.map(b => (
                      <tr key={`${b.centro_id}-${b.insumo_id}`}>
                        <td className="recio">{b.centro}</td>
                        <td>{b.insumo}</td>
                        <td className="num" style={{ color: 'var(--tinta-tenue)' }}>
                          {peso(b.enviado, b.unidad)}
                        </td>
                        <td className="num">{peso(b.usado, b.unidad)}</td>
                        <td className="num recio"
                            style={{ color: b.queda <= 0 ? 'var(--malo)' : 'var(--bueno)' }}>
                          {peso(b.queda, b.unidad)}
                          {b.unidad_compra && b.equivale > 0 && b.queda > 0 && (
                            <div style={{ fontSize: 11, fontWeight: 400,
                                          color: 'var(--tinta-tenue)' }}>
                              {enUnidadDeCompra(b.queda, b.equivale)} {b.unidad_compra}
                              {enUnidadDeCompra(b.queda, b.equivale) === 1 ? '' : 's'}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Lo que no tiene ruta puesta. Esconderlo haría que la suma de las
              rutas no cuadrara con la producción, y ese descuadre es el que
              hace desconfiar de una pantalla entera. */}
          {sinRuta.cuajadas > 0 && (
            <div className="aviso ojo">
              <strong>
                {sinRuta.cuajadas} {sinRuta.cuajadas === 1 ? 'cuajada' : 'cuajadas'} sin
                ruta anotada
              </strong>{' '}
              — {peso(sinRuta.kilos, 'kg')} que no entran en ninguna de las de
              arriba. Se les puede poner la ruta desde 🧀 Producción.
            </div>
          )}

          <p style={{ fontSize: 13, color: 'var(--tinta-tenue)' }}>
            El puesto lo decide el <strong>cumplimiento</strong>, no los kilos por
            tina. La ruta a la que le toca más leche de búfala saca más kilos por
            tina sin hacer nada mejor — le repartieron mejor leche. El
            cumplimiento compara contra lo que <em>esa</em> leche debía dar.
          </p>
        </>
      )}
    </>
  );
}

export default Rutas;
